// ─────────────────────────────────────────────────────────────────────────────
// dp-calendar.js
// Gemeinsamer Monatskalender für alle Datepicker im Shop:
//   • Termin-Auswahl auf der Produktseite   (dp-variant-datepicker.js)
//   • Datumsfilter auf Collection-Seiten     (dp-collection-filters.js)
//
// Der Kalender zeichnet nur Navigation, Wochentage und Tage. Was ein Tag
// bedeutet (wählbar, ausgewählt, Uhrzeit …) und was beim Klick passiert,
// entscheidet der jeweilige Aufrufer über getDay() und onSelect().
//
// Klassisches Script (kein Modul) → als window.DpCalendar überall nutzbar.
// Optik: Abschnitt "Kalender (dpc)" in dp-custom.css.
// ─────────────────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  if (window.DpCalendar) return; // doppelt eingebunden → nichts tun

  // ─── Datum-Helfer ────────────────────────────────────────────────────────
  const TZ = 'Europe/Berlin';
  const MONTHS = ['Jan', 'Feb', 'März', 'Apr', 'Mai', 'Juni', 'Juli', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const WEEKDAYS_SHORT = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];

  // 'en-CA' liefert direkt YYYY-MM-DD
  const _keyFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const _timeFmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: TZ, hour: '2-digit', minute: '2-digit'
  });
  const _longFmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
  const _keyCache = new Map();

  const pad = (n) => String(n).padStart(2, '0');

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  // Tages-Keys (YYYY-MM-DD) werden als UTC-Datum gerechnet → keine DST-Probleme
  function toKey(y, m, d) {
    const dt = new Date(Date.UTC(y, m, d));
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
  }

  function keyToUTCDate(key) {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }

  // Unix-Timestamp (Sekunden) → YYYY-MM-DD in Berliner Zeit
  function tsToKey(ts) {
    let key = _keyCache.get(ts);
    if (key === undefined) {
      key = _keyFmt.format(new Date(ts * 1000));
      _keyCache.set(ts, key);
    }
    return key;
  }

  // Unix-Timestamp → "18:00" in Berliner Zeit
  function tsToTime(ts) {
    return _timeFmt.format(new Date(ts * 1000));
  }

  function todayKey() {
    return _keyFmt.format(new Date());
  }

  // "Mi., 04.11.2026"
  function formatShort(key) {
    const dt = keyToUTCDate(key);
    return `${WEEKDAYS_SHORT[dt.getUTCDay()]}, ${pad(dt.getUTCDate())}.${pad(dt.getUTCMonth() + 1)}.${dt.getUTCFullYear()}`;
  }

  // "Mittwoch, 4. November 2026" (für Screenreader)
  function formatLong(key) {
    return _longFmt.format(keyToUTCDate(key));
  }


  // ─── Kalender ────────────────────────────────────────────────────────────
  const ICON_PREV = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg>';
  const ICON_NEXT = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>';

  /**
   * new DpCalendar(rootElement, options)
   *
   * options:
   *   getDay(key)   → null (kein Termin) oder {
   *                     selectable: bool,   // klickbar
   *                     selected:   bool,   // dunkel hinterlegt
   *                     soldout:    bool,   // Termin vorhanden, aber nicht buchbar
   *                     low:        bool,   // wenige Tickets (oranger Balken)
   *                     sub:        string, // kleine Zeile unter der Zahl, z. B. "18:00"
   *                     label:      string  // Zusatz für Screenreader
   *                   }
   *   onSelect(key)        Klick auf einen wählbaren Tag
   *   onViewChange(view)   Monat gewechselt → { year, month }
   *   minMonth / maxMonth  'YYYY-MM' – Grenzen fürs Blättern (optional)
   *   showBars             Balken unter Terminen anzeigen (Standard: true)
   *   resetLabel, onReset  optionaler "Zurücksetzen"-Link unter dem Kalender
   *   applyLabel, onApply  optionaler "Anwenden"-Button unter dem Kalender
   *                        (Text/Zustand später per setFooter() änderbar)
   *   view                 Startmonat als Key 'YYYY-MM-DD' (Standard: heute)
   */
  class DpCalendar {
    constructor(root, options) {
      this.root = root;
      this.opts = Object.assign({
        getDay: () => null,
        onSelect: () => {},
        onViewChange: () => {},
        minMonth: null,
        maxMonth: null,
        showBars: true,
        resetLabel: null,
        onReset: null,
        applyLabel: null,
        onApply: null,
        view: null
      }, options);

      this.setView(this.opts.view || todayKey(), { silent: true });
      this._build();
    }

    _build() {
      this.root.classList.add('dpc');
      // Ohne Balken entfällt der Platz darunter → Zahlen mittig
      this.root.classList.toggle('dpc--no-bars', !this.opts.showBars);
      this.root.innerHTML =
        '<div class="dpc__nav">'
        + `<button type="button" class="dpc__nav-btn" data-dir="-1" aria-label="Vorheriger Monat">${ICON_PREV}</button>`
        + '<span class="dpc__month" aria-live="polite"></span>'
        + `<button type="button" class="dpc__nav-btn" data-dir="1" aria-label="Nächster Monat">${ICON_NEXT}</button>`
        + '</div>'
        + '<div class="dpc__dow" aria-hidden="true">'
        + '<span>Mo</span><span>Di</span><span>Mi</span><span>Do</span><span>Fr</span><span>Sa</span><span>So</span>'
        + '</div>'
        + '<div class="dpc__weeks"></div>'
        + (this.opts.resetLabel || this.opts.applyLabel
          ? '<div class="dpc__footer">'
            + (this.opts.resetLabel ? `<button type="button" class="dpc__reset">${esc(this.opts.resetLabel)}</button>` : '')
            + (this.opts.applyLabel ? `<button type="button" class="dpc__apply">${esc(this.opts.applyLabel)}</button>` : '')
            + '</div>'
          : '');

      this.monthEl = this.root.querySelector('.dpc__month');
      this.prevBtn = this.root.querySelector('.dpc__nav-btn[data-dir="-1"]');
      this.nextBtn = this.root.querySelector('.dpc__nav-btn[data-dir="1"]');
      this.weeksEl = this.root.querySelector('.dpc__weeks');

      this.prevBtn.addEventListener('click', () => this.shift(-1));
      this.nextBtn.addEventListener('click', () => this.shift(1));

      this.weeksEl.addEventListener('click', (e) => {
        const btn = e.target.closest('.dpc__day');
        if (btn && !btn.disabled) this.opts.onSelect(btn.dataset.key);
      });

      this.resetBtn = this.root.querySelector('.dpc__reset');
      this.applyBtn = this.root.querySelector('.dpc__apply');
      if (this.resetBtn && this.opts.onReset) this.resetBtn.addEventListener('click', () => this.opts.onReset());
      if (this.applyBtn && this.opts.onApply) this.applyBtn.addEventListener('click', () => this.opts.onApply());
    }

    // Footer-Buttons aktualisieren, z. B. setFooter({ applyLabel: 'Filtern (2)', applyDisabled: false })
    setFooter({ applyLabel, applyDisabled, resetDisabled } = {}) {
      if (this.applyBtn) {
        if (applyLabel !== undefined) this.applyBtn.textContent = applyLabel;
        if (applyDisabled !== undefined) this.applyBtn.disabled = applyDisabled;
      }
      if (this.resetBtn && resetDisabled !== undefined) this.resetBtn.disabled = resetDisabled;
    }

    // ─── Ansicht ───────────────────────────────────────────────────────────
    setView(key, { silent = false } = {}) {
      this.year = Number(key.slice(0, 4));
      this.month = Number(key.slice(5, 7)) - 1;
      if (!silent) this.opts.onViewChange({ year: this.year, month: this.month });
    }

    viewMonth() {
      return `${this.year}-${pad(this.month + 1)}`;
    }

    shift(dir) {
      this.setView(toKey(this.year, this.month + dir, 1));
      this.render();
    }

    setBounds(minMonth, maxMonth) {
      this.opts.minMonth = minMonth;
      this.opts.maxMonth = maxMonth;
    }

    // ─── Zeichnen ──────────────────────────────────────────────────────────
    render() {
      const { minMonth, maxMonth, getDay, showBars } = this.opts;
      const month = this.viewMonth();
      const today = todayKey();

      this.monthEl.textContent = `${MONTHS[this.month]} ${this.year}`;
      this.prevBtn.disabled = !!minMonth && month <= minMonth;
      this.nextBtn.disabled = !!maxMonth && month >= maxMonth;

      const offset = (new Date(Date.UTC(this.year, this.month, 1)).getUTCDay() + 6) % 7;
      const daysInMonth = new Date(Date.UTC(this.year, this.month + 1, 0)).getUTCDate();
      const cells = Math.ceil((offset + daysInMonth) / 7) * 7;

      let html = '';
      for (let i = 0; i < cells; i++) {
        if (i % 7 === 0) html += '<div class="dpc__week">';

        const key = toKey(this.year, this.month, 1 - offset + i);
        const day = getDay(key) || null;
        const hasEvent = !!day;

        const cls = ['dpc__day'];
        if (key.slice(0, 7) !== month) cls.push('dpc__day--outside');
        if (key === today) cls.push('dpc__day--today');
        if (hasEvent) cls.push(day.soldout ? 'dpc__day--soldout' : 'dpc__day--event');
        if (day && day.low) cls.push('dpc__day--low');
        if (day && day.selected) cls.push('dpc__day--selected');

        let label = formatLong(key);
        if (!hasEvent) label += ', kein Termin';
        else if (day.soldout) label += ', ausverkauft';
        if (day && day.label) label += `, ${day.label}`;

        html += `<button type="button" class="${cls.join(' ')}" data-key="${key}"`
          + ` aria-label="${esc(label)}"`
          + (day && day.selected ? ' aria-pressed="true"' : '')
          + (day && day.selectable ? '' : ' disabled')
          + `><span class="dpc__day-num">${Number(key.slice(8))}</span>`
          + (day && day.sub ? `<span class="dpc__day-sub">${esc(day.sub)}</span>` : '')
          + (showBars && hasEvent ? '<span class="dpc__bar" aria-hidden="true"></span>' : '')
          + '</button>';

        if (i % 7 === 6) html += '</div>';
      }
      this.weeksEl.innerHTML = html;
    }
  }

  DpCalendar.util = {
    TZ, pad, esc, toKey, keyToUTCDate, tsToKey, tsToTime, todayKey, formatShort, formatLong
  };

  window.DpCalendar = DpCalendar;

  // Scripts, die vor dieser Datei geladen wurden, warten auf dieses Event
  document.dispatchEvent(new CustomEvent('dp:calendar-ready'));
})();